import { useSelector } from 'react-redux';
import _ from 'lodash';
import { useRef, useEffect } from 'react';

export const useChangeDetection = (formType: string) => {
  const formData = useSelector((state: any) => state.forms[formType]?.data);

  const initialSourceConfigRef = useRef<any>(null);
  const initialApiConfigRef = useRef<any>(null);
  const initialResourceConfigRef = useRef<any>(null);
  const initialSelectedFeedsRef = useRef<any>(null);
  const previousFormDataRef = useRef<any>(null);
  const previousSelectedConnectionRef = useRef<any>(undefined);

  // Set initial data in refs when formData is available
  useEffect(() => {
    if (!formData) return;

    const sourceConfig = formData.sourceConfig;
    const apiConfig = formData.apiConfig;
    const resourceConfig = formData.resourceConfig;
    const selectedFeeds = formData.selectedFeeds;

    if (sourceConfig && !initialSourceConfigRef.current) {
      initialSourceConfigRef.current = _.cloneDeep(sourceConfig);
    }

    if (apiConfig && !initialApiConfigRef.current) {
      initialApiConfigRef.current = _.cloneDeep(apiConfig);
    }

    if (resourceConfig && !initialResourceConfigRef.current) {
      initialResourceConfigRef.current = _.cloneDeep(resourceConfig);
    }

    // Track selectedFeeds for ELT flow change detection
    const hasSelectedFeeds = selectedFeeds !== undefined;
    if (hasSelectedFeeds && initialSelectedFeedsRef.current === null) {
      initialSelectedFeedsRef.current = _.cloneDeep(selectedFeeds);
    }
  }, [formData]);

  // Re-baseline dirty detection when the user picks a different connector (partial form reset).
  useEffect(() => {
    if (!formData) return;

    const selectedConnection = formData.selectedConnection;
    const previousSelectedConnection = previousSelectedConnectionRef.current;
    const sourceConfig = formData.sourceConfig;
    const apiConfig = formData.apiConfig;
    const resourceConfig = formData.resourceConfig;
    const selectedFeeds = formData.selectedFeeds;
    const selectedConnectionChanged =
      previousSelectedConnection !== undefined &&
      selectedConnection !== undefined &&
      previousSelectedConnection !== selectedConnection;

    if (selectedConnectionChanged) {
      initialSourceConfigRef.current = sourceConfig ? _.cloneDeep(sourceConfig) : null;
      initialApiConfigRef.current = apiConfig ? _.cloneDeep(apiConfig) : null;
      initialResourceConfigRef.current = resourceConfig ? _.cloneDeep(resourceConfig) : null;
      initialSelectedFeedsRef.current =
        selectedFeeds !== undefined ? _.cloneDeep(selectedFeeds) : null;
    }
    previousSelectedConnectionRef.current = selectedConnection;
  }, [formData]);

  // Reset initial refs when form data changes significantly (e.g., after save operation)
  useEffect(() => {
    const previousFormData = previousFormDataRef.current;
    if (!formData || !previousFormData) {
      previousFormDataRef.current = formData ? _.cloneDeep(formData) : null;
      return;
    }

    const sourceConfig = formData.sourceConfig;
    const apiConfig = formData.apiConfig;
    const resourceConfig = formData.resourceConfig;
    const selectedFeeds = formData.selectedFeeds;

    // Check if this looks like a save operation (form fields being cleared)
    const isCredentialCleared = previousFormData.credentialId && !formData.credentialId;
    const isSelectedConnectionCleared =
      previousFormData.selectedConnection && !formData.selectedConnection;
    const isResourceConfigCleared = previousFormData.resourceConfig && !resourceConfig;
    const isSaveOperation =
      isCredentialCleared || isSelectedConnectionCleared || isResourceConfigCleared;

    if (isSaveOperation) {
      // Reset all initial refs to current form data
      initialSourceConfigRef.current = sourceConfig ? _.cloneDeep(sourceConfig) : null;
      initialApiConfigRef.current = apiConfig ? _.cloneDeep(apiConfig) : null;
      initialResourceConfigRef.current = resourceConfig ? _.cloneDeep(resourceConfig) : null;
      initialSelectedFeedsRef.current =
        selectedFeeds !== undefined ? _.cloneDeep(selectedFeeds) : null;
    }

    // Update previous form data ref
    previousFormDataRef.current = formData ? _.cloneDeep(formData) : null;
  }, [formData]);

  // If we don't have formData, no changes
  if (!formData) {
    return { hasChanges: false };
  }

  const sourceConfig = formData.sourceConfig;
  const apiConfig = formData.apiConfig;
  const resourceConfig = formData.resourceConfig;
  const selectedFeeds = formData.selectedFeeds;
  const flowType = formData.flowType;

  const hasResourceConfigChanges =
    initialResourceConfigRef?.current &&
    !_.isEqual(initialResourceConfigRef.current, resourceConfig);

  // Check for source config changes
  const hasSourceConfigChanges =
    initialSourceConfigRef?.current && !_.isEqual(initialSourceConfigRef.current, sourceConfig);

  // Check for api config changes (at root level)
  const hasApiConfigChanges =
    initialApiConfigRef?.current && !_.isEqual(initialApiConfigRef.current, apiConfig);

  // Check for selectedFeeds changes (ELT flow)
  // Use !== undefined to properly handle empty objects {}
  const hasSelectedFeedsChanges =
    selectedFeeds !== undefined &&
    initialSelectedFeedsRef?.current !== null &&
    !_.isEqual(initialSelectedFeedsRef.current, selectedFeeds);

  let hasUnsavedChanges =
    hasSourceConfigChanges ||
    hasApiConfigChanges ||
    hasResourceConfigChanges ||
    hasSelectedFeedsChanges;

  // Special handling for BYO Flow - check if changes are actually meaningful
  if (flowType === 'custom' && hasSourceConfigChanges) {
    // For BYO Flow, let's check if the changes are just default values being added
    const initialKeys = initialSourceConfigRef?.current
      ? Object.keys(initialSourceConfigRef.current)
      : [];
    const currentKeys = sourceConfig ? Object.keys(sourceConfig) : [];

    // If the initial state was empty and current state has values, it might be just initialization
    if (initialKeys.length === 0 && currentKeys.length > 0) {
      hasUnsavedChanges = false;
    }

    // Check if the only differences are default fields like name and description
    const addedKeys = currentKeys.filter((key) => !initialKeys.includes(key));
    const removedKeys = initialKeys.filter((key) => !currentKeys.includes(key));

    // Common default fields that are added automatically
    const defaultFields = ['name', 'description', 'sourceName', 'sourceDescription'];
    const onlyDefaultFieldsAdded =
      addedKeys.every((key) => defaultFields.includes(key)) && removedKeys.length === 0;

    if (onlyDefaultFieldsAdded) {
      // Even if only default fields were added, we need to check if their values actually changed
      // Check if any of the added default fields have meaningful values (not empty/default)
      const hasMeaningfulChanges = addedKeys.some((key) => {
        if (!sourceConfig) return false;

        const currentValue = sourceConfig[key];

        // If the field has a meaningful value (not empty, not default), consider it a real change
        // For BYO Flow, let's be more conservative - only consider it meaningful if it's clearly user-modified
        const hasMeaningfulDefaultFieldValue =
          currentValue &&
          currentValue.trim &&
          currentValue.trim() !== '' &&
          currentValue !== '10k_files_1mln_records';

        return hasMeaningfulDefaultFieldValue;
      });

      if (!hasMeaningfulChanges) {
        hasUnsavedChanges = false;
      }
    }
  }

  return { hasChanges: hasUnsavedChanges };
};
